import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import { Calculator, Sparkles, Loader2, TrendingUp, AlertTriangle, ListChecks, Tag, Save, Check, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import RehabEstimator from "@/components/RehabEstimator";
import RehabRenderer from "@/components/RehabRenderer";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";

const TABS = [
  { id: "wholesale", label: "Wholesale" },
  { id: "novation", label: "Novation" },
  { id: "wholetail", label: "Wholetail" },
  { id: "subto", label: "Sub-To" },
  { id: "flip", label: "Fix & Flip" },
  { id: "brrrr", label: "BRRRR" },
  { id: "rental", label: "Rental" },
];

const VERDICT_STYLE = {
  GO: { bg: "rgba(16,185,129,0.12)", color: "#047857", border: "rgba(16,185,129,0.35)" },
  MAYBE: { bg: "rgba(245,158,11,0.12)", color: "#B45309", border: "rgba(245,158,11,0.35)" },
  PASS: { bg: "rgba(231,111,81,0.14)", color: "#B91C1C", border: "rgba(231,111,81,0.35)" },
};

const VALID_TABS = new Set(["wholesale", "novation", "wholetail", "subto", "flip", "brrrr", "rental"]);

export default function Analyzer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (() => {
    const q = searchParams.get("tab");
    return q && VALID_TABS.has(q) ? q : "wholesale";
  })();
  const [tab, setTab] = useState(initialTab);
  const [rendererOpen, setRendererOpen] = useState(false);
  // Carry inputs across tabs (e.g., wholesale → novation cross-sell).
  const [crossPrefill, setCrossPrefill] = useState(null);
  const switchTo = (id, prefill = null) => {
    setCrossPrefill(prefill);
    setTab(id);
    setSearchParams({ tab: id }, { replace: true });
  };

  // Respond to query-param changes (e.g., back/forward, deep links).
  useEffect(() => {
    const q = searchParams.get("tab");
    if (q && VALID_TABS.has(q) && q !== tab) {
      setTab(q);
    }
  }, [searchParams]);

  return (
    <div data-testid="analyzer-page">
      <div className="topbar">
        <div>
          <div className="crumb">Deal Analyzer</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">Multi-Strategy Deal Analyzer</h1>
          <p className="text-[var(--ink-2)] text-sm mt-1">One tool. Seven strategies. Pick how you&apos;d run this deal.</p>
        </div>
        <div className="ml-auto">
          <button
            type="button"
            onClick={() => setRendererOpen(true)}
            className="btn-outline flex items-center gap-2"
            data-testid="analyzer-open-rehab-renderer"
            title="AI-generate a photo-realistic 'after' render from any property photo"
          >
            <ImagePlus size={14} />
            <span className="font-semibold">AI After-Rehab Renderer</span>
            <span className="ml-1 text-[10px] uppercase tracking-widest font-bold bg-[var(--primary)]/20 text-[var(--brand-primary-dark)] px-1.5 py-[2px] rounded">New</span>
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-6 border-b border-[var(--line)] overflow-x-auto" data-testid="analyzer-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => switchTo(t.id)}
            className={"px-5 py-3 text-sm font-semibold border-b-2 transition-colors -mb-px whitespace-nowrap " +
              (tab === t.id ? "border-[var(--ink)] text-[var(--ink)]" : "border-transparent text-[var(--ink-2)] hover:text-[var(--ink)]")}
            data-testid={`analyzer-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "wholesale" && <Wholesale onSwitchTo={switchTo} />}
      {tab === "novation" && <Novation prefill={crossPrefill} />}
      {tab === "wholetail" && <Wholetail prefill={crossPrefill} />}
      {tab === "subto" && <SubTo />}
      {tab === "flip" && <Flip />}
      {tab === "brrrr" && <BRRRR />}
      {tab === "rental" && <Rental />}

      <RehabRenderer open={rendererOpen} onClose={() => setRendererOpen(false)} />
    </div>
  );
}

/* ─── Wholesale (unchanged behavior + AI) ─── */
function Wholesale({ onSwitchTo }) {
  const [form, setForm] = useState({
    arv: "385000", repair_cost: "38000", purchase_price: "230000",
    assignment_fee: "10000", rule_percent: "70",
    property_address: "412 Maple Ave, Austin TX", notes: "",
  });
  const [metrics, setMetrics] = useState(null);
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [error, setError] = useState("");
  const [bestExit, setBestExit] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const payload = () => ({
    arv: Number(form.arv), repair_cost: Number(form.repair_cost),
    purchase_price: form.purchase_price === "" ? null : Number(form.purchase_price),
    assignment_fee: Number(form.assignment_fee), rule_percent: Number(form.rule_percent),
  });

  const calc = async (e) => {
    e?.preventDefault();
    setError(""); setInsight(null); setLoading(true);
    setBestExit(null);
    try {
      const { data } = await api.post("/analyzer/calculate", payload());
      setMetrics(data);
      // Fetch the best-exit comparison in parallel — non-blocking.
      api.post("/analyzer/best-exit", payload())
        .then((r) => setBestExit(r.data))
        .catch(() => setBestExit(null));
    }
    catch (err) { setError(err?.response?.data?.detail || "Calc failed"); }
    finally { setLoading(false); }
  };
  const askAi = async () => {
    setError(""); setAiLoading(true);
    try {
      const { data } = await api.post("/analyzer/ai-insight", { ...payload(), property_address: form.property_address, notes: form.notes });
      setMetrics(data.metrics); setInsight(data.insight);
    } catch (err) { setError(err?.response?.data?.detail || "AI insight failed"); }
    finally { setAiLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-5">
      <form onSubmit={calc} className="card lg:col-span-2">
        <div className="label-eyebrow">Wholesale Inputs</div>
        <h2 className="font-display font-bold text-xl mt-1 mb-5">70% Rule MAO</h2>
        <div className="flex flex-col gap-4">
          <div>
            <label className="field-label">Property (optional)</label>
            <AddressAutocomplete
              name="property_address"
              placeholder="Start typing — we'll suggest verified addresses"
              inputClassName="input"
              dataTestid="analyzer-address"
              value={form.property_address}
              onChange={(v) => setForm((f) => ({ ...f, property_address: v }))}
              onParsed={({ street, city, state, zip }) => setForm((prev) => ({
                ...prev,
                property_address: street || prev.property_address,
                city: city || prev.city,
                state: state || prev.state,
                zip: zip || prev.zip,
              }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="field-label">ARV *</label><input className="input" type="number" required min="1" value={form.arv} onChange={set("arv")} data-testid="analyzer-arv" /></div>
            <div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <label className="field-label !mb-0">Repair cost</label>
                <RehabEstimator
                  sqft={Number(form.sqft) || null}
                  state={form.state || null}
                  leadId={null}
                  onPick={(val) => setForm((f) => ({ ...f, repair_cost: String(val) }))}
                />
              </div>
              <input className="input" type="number" min="0" value={form.repair_cost} onChange={set("repair_cost")} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="field-label">Purchase price</label><input className="input" type="number" min="0" value={form.purchase_price} onChange={set("purchase_price")} /></div>
            <div><label className="field-label">Assignment fee</label><input className="input" type="number" min="0" value={form.assignment_fee} onChange={set("assignment_fee")} /></div>
          </div>
          <div><label className="field-label">Rule %</label><input className="input" type="number" min="50" max="90" value={form.rule_percent} onChange={set("rule_percent")} /></div>
          <div><label className="field-label">Notes for AI</label><textarea className="textarea" rows={3} value={form.notes} onChange={set("notes")} /></div>
          {error && <div className="banner-err">{String(error)}</div>}
          <div className="flex gap-3">
            <button type="submit" className="btn-primary" disabled={loading} data-testid="analyzer-calculate-button"><Calculator size={14} /> {loading ? "Calculating…" : "Calculate"}</button>
            <button type="button" onClick={askAi} className="btn-money" disabled={aiLoading || !form.arv} data-testid="analyzer-ai-button">
              {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {aiLoading ? "Thinking…" : "AI Verdict"}
            </button>
          </div>
        </div>
      </form>

      <div className="lg:col-span-3 flex flex-col gap-5">
        <ResultsCard
          title="Wholesale Math"
          empty="Enter a deal and hit Calculate to see MAO, profit, ROI."
          metrics={metrics && [
            { label: `MAO (${metrics.rule_percent}%)`, value: fmtMoney(metrics.mao), accent: true, big: true },
            { label: "Projected Profit", value: fmtMoney(metrics.projected_profit), accent: metrics.projected_profit > 0, big: true },
            { label: "ROI", value: fmtPct(metrics.roi_percent) },
            { label: "Assignment Fee", value: fmtMoney(metrics.assignment_fee) },
          ]}
          rec={metrics?.recommendation}
        />
        {/* Best-Exit badge — auto-ranked across Wholesale / Novation / Wholetail */}
        {bestExit && metrics && onSwitchTo && (
          <BestExitBadge
            data={bestExit}
            onJump={(strategy) => {
              if (strategy === "wholesale") return; // already here
              onSwitchTo(strategy, {
                arv: form.arv,
                seller_net_target: form.purchase_price,
                purchase_price: form.purchase_price,
                light_rehab_cost: form.repair_cost,
              });
            }}
          />
        )}
        {/* Cross-sell: when the wholesale spread is too thin to assign, nudge
            the user toward Novation or Wholetail with their inputs pre-loaded. */}
        {metrics && metrics.projected_profit < 8000 && onSwitchTo && (
          <CrossSellCard
            wholesaleProfit={metrics.projected_profit}
            onTryNovation={() => onSwitchTo("novation", {
              arv: form.arv,
              seller_net_target: form.purchase_price || metrics.mao,
              light_rehab_cost: form.repair_cost,
            })}
            onTryWholetail={() => onSwitchTo("wholetail", {
              arv: form.arv,
              purchase_price: form.purchase_price || metrics.mao,
              light_rehab_cost: form.repair_cost,
            })}
          />
        )}
        {insight && <InsightCard insight={insight} />}
      </div>
    </div>
  );
}

/* ─── Fix & Flip ─── */
function Flip() {
  const [form, setForm] = useState({
    arv: "300000", purchase_price: "160000", rehab_cost: "45000",
    closing_costs: "4000", holding_costs: "8000", selling_costs_pct: "6",
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const { data } = await api.post("/analyzer/flip", {
        arv: Number(form.arv), purchase_price: Number(form.purchase_price), rehab_cost: Number(form.rehab_cost),
        closing_costs: Number(form.closing_costs), holding_costs: Number(form.holding_costs),
        selling_costs_pct: Number(form.selling_costs_pct),
      });
      setResult(data);
    } catch (err) { setError(err?.response?.data?.detail || "Calc failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-5">
      <form onSubmit={submit} className="card lg:col-span-2">
        <div className="label-eyebrow">Fix & Flip Inputs</div>
        <h2 className="font-display font-bold text-xl mt-1 mb-5">Profit & ROI</h2>
        <div className="flex flex-col gap-4">
          <NumField label="ARV *" v={form.arv} onChange={set("arv")} tid="flip-arv" />
          <NumField label="Purchase price" v={form.purchase_price} onChange={set("purchase_price")} tid="flip-purchase" />
          <NumField label="Rehab cost" v={form.rehab_cost} onChange={set("rehab_cost")} right={
            <RehabEstimator sqft={Number(form.sqft) || null} state={form.state || null} leadId={null}
              onPick={(val) => setForm((f) => ({ ...f, rehab_cost: String(val) }))} />
          } />
          <NumField label="Closing costs (buy + sell)" v={form.closing_costs} onChange={set("closing_costs")} />
          <NumField label="Holding costs (taxes, utilities, debt service)" v={form.holding_costs} onChange={set("holding_costs")} />
          <NumField label="Selling costs %" v={form.selling_costs_pct} onChange={set("selling_costs_pct")} step="0.1" />
          {error && <div className="banner-err">{String(error)}</div>}
          <button type="submit" className="btn-money" disabled={loading} data-testid="flip-calc-button">
            <Calculator size={14} /> {loading ? "…" : "Run Flip Analysis"}
          </button>
        </div>
      </form>
      <div className="lg:col-span-3">
        <ResultsCard
          title="Flip Math"
          empty="Run the analysis to see total costs, profit, and ROI."
          metrics={result && [
            { label: "Total Costs", value: fmtMoney(result.total_costs) },
            { label: "Selling Costs", value: fmtMoney(result.selling_costs) },
            { label: "Projected Profit", value: fmtMoney(result.projected_profit), big: true, accent: result.projected_profit > 0 },
            { label: "ROI", value: fmtPct(result.roi_percent), big: true, accent: true },
          ]}
          rec={result?.recommendation}
        />
      </div>
    </div>
  );
}

/* ─── BRRRR ─── */
function BRRRR() {
  const [form, setForm] = useState({
    arv: "180000", purchase_price: "78000", rehab_cost: "38000",
    refi_ltv_pct: "75", interest_rate_pct: "7.5", loan_term_years: "30",
    monthly_rent: "1450", monthly_expenses: "480",
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, Number(v)]));
      const { data } = await api.post("/analyzer/brrrr", body);
      setResult(data);
    } catch (err) { setError(err?.response?.data?.detail || "Calc failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-5">
      <form onSubmit={submit} className="card lg:col-span-2">
        <div className="label-eyebrow">BRRRR Inputs</div>
        <h2 className="font-display font-bold text-xl mt-1 mb-5">Refi · Rent · Repeat</h2>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <NumField label="ARV *" v={form.arv} onChange={set("arv")} tid="brrrr-arv" />
            <NumField label="Purchase price" v={form.purchase_price} onChange={set("purchase_price")} />
          </div>
          <NumField label="Rehab cost" v={form.rehab_cost} onChange={set("rehab_cost")} right={
            <RehabEstimator sqft={Number(form.sqft) || null} state={form.state || null} leadId={null}
              onPick={(val) => setForm((f) => ({ ...f, rehab_cost: String(val) }))} />
          } />
          <div className="grid grid-cols-3 gap-3">
            <NumField label="Refi LTV %" v={form.refi_ltv_pct} onChange={set("refi_ltv_pct")} step="1" />
            <NumField label="Rate %" v={form.interest_rate_pct} onChange={set("interest_rate_pct")} step="0.1" />
            <NumField label="Term (yrs)" v={form.loan_term_years} onChange={set("loan_term_years")} step="1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <NumField label="Monthly rent" v={form.monthly_rent} onChange={set("monthly_rent")} />
            <NumField label="Monthly expenses" v={form.monthly_expenses} onChange={set("monthly_expenses")} />
          </div>
          {error && <div className="banner-err">{String(error)}</div>}
          <button type="submit" className="btn-money" disabled={loading} data-testid="brrrr-calc-button">
            <Calculator size={14} /> {loading ? "…" : "Run BRRRR Analysis"}
          </button>
        </div>
      </form>
      <div className="lg:col-span-3">
        <ResultsCard
          title="BRRRR Math"
          empty="Run to see refi loan, cash left in, and CoC return."
          metrics={result && [
            { label: "Refi Loan", value: fmtMoney(result.refi_loan), big: true },
            { label: "Cash Left in Deal", value: fmtMoney(result.cash_left_in_deal), big: true, accent: result.cash_left_in_deal === 0 },
            { label: "Cash Pulled Out", value: fmtMoney(result.cash_pulled_out), accent: result.cash_pulled_out > 0 },
            { label: "Monthly P&I", value: fmtMoney(result.monthly_pi) },
            { label: "Monthly Cash Flow", value: fmtMoney(result.monthly_cash_flow), accent: result.monthly_cash_flow > 0 },
            { label: "Cash-on-Cash", value: result.cash_on_cash_pct === "Infinite" ? "∞" : fmtPct(result.cash_on_cash_pct), accent: true },
          ]}
          rec={result?.recommendation}
        />
      </div>
    </div>
  );
}

/* ─── Rental ─── */
function Rental() {
  const [form, setForm] = useState({
    purchase_price: "215000", down_payment_pct: "20", interest_rate_pct: "7.25",
    loan_term_years: "30", monthly_rent: "1750", monthly_expenses: "550",
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, Number(v)]));
      const { data } = await api.post("/analyzer/rental", body);
      setResult(data);
    } catch (err) { setError(err?.response?.data?.detail || "Calc failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-5">
      <form onSubmit={submit} className="card lg:col-span-2">
        <div className="label-eyebrow">Rental Inputs</div>
        <h2 className="font-display font-bold text-xl mt-1 mb-5">Cap Rate · Cash-on-Cash</h2>
        <div className="flex flex-col gap-4">
          <NumField label="Purchase price *" v={form.purchase_price} onChange={set("purchase_price")} tid="rental-purchase" />
          <div className="grid grid-cols-3 gap-3">
            <NumField label="Down %" v={form.down_payment_pct} onChange={set("down_payment_pct")} step="1" />
            <NumField label="Rate %" v={form.interest_rate_pct} onChange={set("interest_rate_pct")} step="0.1" />
            <NumField label="Term (yrs)" v={form.loan_term_years} onChange={set("loan_term_years")} step="1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <NumField label="Monthly rent" v={form.monthly_rent} onChange={set("monthly_rent")} />
            <NumField label="Monthly expenses" v={form.monthly_expenses} onChange={set("monthly_expenses")} />
          </div>
          {error && <div className="banner-err">{String(error)}</div>}
          <button type="submit" className="btn-money" disabled={loading} data-testid="rental-calc-button">
            <Calculator size={14} /> {loading ? "…" : "Run Rental Analysis"}
          </button>
        </div>
      </form>
      <div className="lg:col-span-3">
        <ResultsCard
          title="Rental Math"
          empty="Run to see cap rate, cash-on-cash, and monthly cash flow."
          metrics={result && [
            { label: "Cap Rate", value: fmtPct(result.cap_rate_pct), big: true, accent: true },
            { label: "Cash-on-Cash", value: fmtPct(result.cash_on_cash_pct), big: true, accent: result.cash_on_cash_pct > 0 },
            { label: "Monthly Cash Flow", value: fmtMoney(result.monthly_cash_flow), accent: result.monthly_cash_flow > 0 },
            { label: "Monthly P&I", value: fmtMoney(result.monthly_pi) },
            { label: "Down Payment", value: fmtMoney(result.down_payment) },
            { label: "GRM", value: result.gross_rent_multiplier },
          ]}
          rec={result?.recommendation}
        />
      </div>
    </div>
  );
}

/* ─── Shared widgets ─── */
function NumField({ label, v, onChange, step = "1", tid, right }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1">
        <label className="field-label !mb-0">{label}</label>
        {right}
      </div>
      <input className="input" type="number" min="0" step={step} value={v} onChange={onChange} data-testid={tid} />
    </div>
  );
}

/* ─── Novation ─── */
function Novation({ prefill }) {
  const [form, setForm] = useState(() => ({
    arv: prefill?.arv ?? "385000",
    seller_net_target: prefill?.seller_net_target ?? "230000",
    light_rehab_cost: prefill?.light_rehab_cost ?? "8000",
    days_on_market: "21",
    monthly_holding_cost: "1500",
    listing_agent_pct: "3",
    buyer_agent_pct: "3",
    closing_costs_pct: "1.5",
  }));
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, Number(v)]));
      const { data } = await api.post("/analyzer/novation", body);
      setResult(data);
    } catch (err) { setError(err?.response?.data?.detail || "Calc failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-5">
      <form onSubmit={submit} className="card lg:col-span-2" data-testid="novation-form">
        <div className="label-eyebrow">Novation Inputs</div>
        <h2 className="font-display font-bold text-xl mt-1 mb-5">List-on-MLS spread</h2>
        <p className="text-xs text-[var(--ink-3)] mb-4 -mt-3">
          Seller keeps title until close. You market on MLS, split the spread above their net target.
        </p>
        <div className="flex flex-col gap-4">
          <NumField label="ARV (likely list price) *" v={form.arv} onChange={set("arv")} tid="novation-arv" />
          <NumField label="Seller's net target *" v={form.seller_net_target} onChange={set("seller_net_target")} />
          <NumField label="Light rehab (paint/clean/staging)" v={form.light_rehab_cost} onChange={set("light_rehab_cost")} right={
            <RehabEstimator sqft={Number(form.sqft) || null} state={form.state || null} leadId={prefill?.lead_id || null}
              onPick={(val) => setForm((f) => ({ ...f, light_rehab_cost: String(val) }))} />
          } />
          <div className="grid grid-cols-2 gap-3">
            <NumField label="Days on market" v={form.days_on_market} onChange={set("days_on_market")} />
            <NumField label="Holding cost / month" v={form.monthly_holding_cost} onChange={set("monthly_holding_cost")} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <NumField label="List agent %" v={form.listing_agent_pct} onChange={set("listing_agent_pct")} step="0.5" />
            <NumField label="Buyer agent %" v={form.buyer_agent_pct} onChange={set("buyer_agent_pct")} step="0.5" />
            <NumField label="Closing %" v={form.closing_costs_pct} onChange={set("closing_costs_pct")} step="0.1" />
          </div>
          {error && <div className="banner-err">{String(error)}</div>}
          <button type="submit" className="btn-money" disabled={loading} data-testid="novation-calc-button">
            <Calculator size={14} /> {loading ? "…" : "Run Novation Analysis"}
          </button>
        </div>
      </form>
      <div className="lg:col-span-3">
        <ResultsCard
          title="Novation Math"
          empty="Enter the deal to see the closer's spread and margin."
          metrics={result && [
            { label: "Closer Profit", value: fmtMoney(result.closer_profit), big: true, accent: result.closer_profit > 0 },
            { label: "Margin %", value: fmtPct(result.margin_pct), big: true },
            { label: "Total Costs", value: fmtMoney(result.total_costs) },
            { label: "List + Buyer Agent", value: fmtMoney(result.listing_agent_costs + result.buyer_agent_costs) },
            { label: "Closing Costs", value: fmtMoney(result.closing_costs) },
            { label: "Holding Est.", value: fmtMoney(result.estimated_holding_cost) },
          ]}
          rec={result?.recommendation}
        />
      </div>
    </div>
  );
}

/* ─── Wholetail ─── */
function Wholetail({ prefill }) {
  const [form, setForm] = useState(() => ({
    arv: prefill?.arv ?? "320000",
    purchase_price: prefill?.purchase_price ?? "195000",
    light_rehab_cost: prefill?.light_rehab_cost ?? "12000",
    list_discount_pct: "5",
    days_to_close: "21",
    monthly_holding_cost: "1200",
    selling_costs_pct: "6",
    closing_costs: "2500",
  }));
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, Number(v)]));
      const { data } = await api.post("/analyzer/wholetail", body);
      setResult(data);
    } catch (err) { setError(err?.response?.data?.detail || "Calc failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-5">
      <form onSubmit={submit} className="card lg:col-span-2" data-testid="wholetail-form">
        <div className="label-eyebrow">Wholetail Inputs</div>
        <h2 className="font-display font-bold text-xl mt-1 mb-5">Light rehab → fast MLS</h2>
        <p className="text-xs text-[var(--ink-3)] mb-4 -mt-3">
          Buy it, clean/paint/stage, list slightly under ARV for a cash close. Wholesale speed, flip-ish margin.
        </p>
        <div className="flex flex-col gap-4">
          <NumField label="ARV *" v={form.arv} onChange={set("arv")} tid="wholetail-arv" />
          <NumField label="Purchase price *" v={form.purchase_price} onChange={set("purchase_price")} />
          <NumField label="Light rehab" v={form.light_rehab_cost} onChange={set("light_rehab_cost")} right={
            <RehabEstimator sqft={Number(form.sqft) || null} state={form.state || null} leadId={prefill?.lead_id || null}
              onPick={(val) => setForm((f) => ({ ...f, light_rehab_cost: String(val) }))} />
          } />
          <div className="grid grid-cols-2 gap-3">
            <NumField label="List discount %" v={form.list_discount_pct} onChange={set("list_discount_pct")} step="0.5" />
            <NumField label="Days to close" v={form.days_to_close} onChange={set("days_to_close")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <NumField label="Holding / month" v={form.monthly_holding_cost} onChange={set("monthly_holding_cost")} />
            <NumField label="Selling %" v={form.selling_costs_pct} onChange={set("selling_costs_pct")} step="0.1" />
          </div>
          <NumField label="Closing costs" v={form.closing_costs} onChange={set("closing_costs")} />
          {error && <div className="banner-err">{String(error)}</div>}
          <button type="submit" className="btn-money" disabled={loading} data-testid="wholetail-calc-button">
            <Calculator size={14} /> {loading ? "…" : "Run Wholetail Analysis"}
          </button>
        </div>
      </form>
      <div className="lg:col-span-3">
        <ResultsCard
          title="Wholetail Math"
          empty="Run to see projected profit + ROI on the cash-close exit."
          metrics={result && [
            { label: "List Price", value: fmtMoney(result.list_price), big: true, accent: true },
            { label: "Projected Profit", value: fmtMoney(result.projected_profit), big: true, accent: result.projected_profit > 0 },
            { label: "ROI", value: fmtPct(result.roi_percent) },
            { label: "Selling Costs", value: fmtMoney(result.selling_costs) },
            { label: "Holding Est.", value: fmtMoney(result.estimated_holding_cost) },
            { label: "Total Costs", value: fmtMoney(result.total_costs) },
          ]}
          rec={result?.recommendation}
        />
      </div>
    </div>
  );
}

/* ─── Sub-To ─── */
function SubTo() {
  const [form, setForm] = useState({
    arv: "340000", loan_balance: "210000", seller_equity_cash: "5000",
    monthly_piti: "1850", monthly_rent: "2200", monthly_expenses: "250",
    closing_costs: "2500",
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, Number(v)]));
      const { data } = await api.post("/analyzer/subto", body);
      setResult(data);
    } catch (err) { setError(err?.response?.data?.detail || "Calc failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-5">
      <form onSubmit={submit} className="card lg:col-span-2" data-testid="subto-form">
        <div className="label-eyebrow">Sub-To Inputs</div>
        <h2 className="font-display font-bold text-xl mt-1 mb-5">Take title · keep the loan</h2>
        <p className="text-xs text-[var(--ink-3)] mb-4 -mt-3">
          Take over the existing mortgage payments. Run as a rental or re-sell. Verify the loan&apos;s due-on-sale risk before closing.
        </p>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <NumField label="ARV *" v={form.arv} onChange={set("arv")} tid="subto-arv" />
            <NumField label="Loan balance" v={form.loan_balance} onChange={set("loan_balance")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <NumField label="Cash to seller" v={form.seller_equity_cash} onChange={set("seller_equity_cash")} />
            <NumField label="Closing costs" v={form.closing_costs} onChange={set("closing_costs")} />
          </div>
          <NumField label="Monthly PITI (taken over)" v={form.monthly_piti} onChange={set("monthly_piti")} />
          <div className="grid grid-cols-2 gap-3">
            <NumField label="Monthly rent" v={form.monthly_rent} onChange={set("monthly_rent")} />
            <NumField label="Monthly expenses" v={form.monthly_expenses} onChange={set("monthly_expenses")} />
          </div>
          {error && <div className="banner-err">{String(error)}</div>}
          <button type="submit" className="btn-money" disabled={loading} data-testid="subto-calc-button">
            <Calculator size={14} /> {loading ? "…" : "Run Sub-To Analysis"}
          </button>
        </div>
      </form>
      <div className="lg:col-span-3">
        <ResultsCard
          title="Sub-To Math"
          empty="Enter the loan + rent to see equity capture and cash flow."
          metrics={result && [
            { label: "Equity Capture", value: fmtMoney(result.equity_capture), big: true, accent: true },
            { label: "Monthly Cash Flow", value: fmtMoney(result.monthly_cash_flow), big: true, accent: result.monthly_cash_flow > 0 },
            { label: "Annual Cash Flow", value: fmtMoney(result.annual_cash_flow) },
            { label: "Cash-on-Cash", value: typeof result.cash_on_cash_pct === "number" ? fmtPct(result.cash_on_cash_pct) : result.cash_on_cash_pct },
            { label: "Cash to Close", value: fmtMoney(result.cash_to_close) },
            { label: "Loan Balance", value: fmtMoney(result.loan_balance) },
          ]}
          rec={result?.recommendation}
        />
      </div>
    </div>
  );
}

/* ─── Best Exit badge — auto-ranks profit across all 4 strategies ─── */
function BestExitBadge({ data, onJump }) {
  const winner = data?.options?.[0];
  const [saveOpen, setSaveOpen] = useState(false);
  const [leads, setLeads] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedTo, setSavedTo] = useState(null);

  const loadLeads = async () => {
    if (leads) return;
    try {
      const { data: list } = await api.get("/leads?limit=50");
      // Accept both shape {leads: []} and []
      const rows = Array.isArray(list) ? list : (list?.leads || []);
      setLeads(rows.filter((l) => (l.stage || "") !== "Dead").slice(0, 50));
    } catch { setLeads([]); }
  };

  const toggleSave = async () => {
    setSaveOpen((v) => !v);
    if (!saveOpen) loadLeads();
  };

  const saveToLead = async (lead) => {
    setSaving(true);
    try {
      await api.put(`/leads/${lead.id}/best-exit`, {
        strategy: winner.strategy,
        label: winner.label,
        profit: winner.profit,
        inputs: data.inputs || {},
      });
      setSavedTo(lead);
      setSaveOpen(false);
      const addr = lead.property_address || lead.city || "the lead";
      toast.success(`Saved ${winner.label} · ${fmtMoney(winner.profit)} → ${addr}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally { setSaving(false); }
  };

  if (!winner) return null;
  const isWholesale = winner.strategy === "wholesale";
  return (
    <div
      className="card overflow-hidden"
      data-testid="best-exit-badge"
      style={{
        background: isWholesale
          ? "linear-gradient(135deg, rgba(16,185,129,0.12), rgba(5,150,105,0.08))"
          : "linear-gradient(135deg, rgba(245,158,11,0.12), rgba(244,63,94,0.08))",
        borderColor: isWholesale ? "rgba(16,185,129,0.4)" : "rgba(245,158,11,0.4)",
      }}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow flex items-center gap-1.5"
               style={{ color: isWholesale ? "#047857" : "#B45309" }}>
            <Sparkles size={11} /> Best Exit
          </div>
          <h3 className="font-display font-bold text-xl mt-1 leading-tight">
            {isWholesale
              ? `Stick with Wholesale — ${fmtMoney(winner.profit)} profit`
              : `Switch to ${winner.label} — ${fmtMoney(winner.profit)} profit`}
          </h3>
          <p className="text-xs text-[var(--ink-3)] mt-1">
            {isWholesale
              ? "Your assignment is the highest-profit path for this deal."
              : `${winner.label} beats Wholesale by ${fmtMoney(winner.profit - (data.options.find((o) => o.strategy === "wholesale")?.profit || 0))} on the same inputs.`}
          </p>
          {savedTo && (
            <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-[var(--line)] text-xs"
                 data-testid="best-exit-saved-pill">
              <Check size={11} className="text-emerald-600" />
              <span>Saved to <strong>{savedTo.property_address || savedTo.city || "lead"}</strong></span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          {!isWholesale && (
            <button
              type="button"
              onClick={() => onJump(winner.strategy)}
              className="btn-money text-sm whitespace-nowrap"
              data-testid="best-exit-jump"
            >
              Run {winner.label} →
            </button>
          )}
          <div className="relative">
            <button
              type="button"
              onClick={toggleSave}
              disabled={saving}
              className="btn-outline text-xs whitespace-nowrap w-full"
              data-testid="best-exit-save-button"
            >
              {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
              {savedTo ? "Save to another lead" : "Save to lead"}
            </button>
            {saveOpen && (
              <div
                className="absolute right-0 mt-1 w-72 z-30 rounded-lg border border-[var(--line)] bg-white shadow-xl max-h-72 overflow-auto"
                data-testid="best-exit-lead-picker"
              >
                <div className="px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-[var(--ink-3)] border-b border-[var(--line)]">
                  Pick a lead to attach
                </div>
                {leads === null && (
                  <div className="p-4 flex items-center justify-center text-xs text-[var(--ink-3)]">
                    <Loader2 size={12} className="animate-spin mr-1" /> Loading…
                  </div>
                )}
                {leads !== null && leads.length === 0 && (
                  <div className="p-4 text-xs text-[var(--ink-3)] text-center">
                    No active leads. Create one from Pipeline first.
                  </div>
                )}
                {leads?.map((l) => (
                  <button
                    type="button"
                    key={l.id}
                    onClick={() => saveToLead(l)}
                    className="block w-full text-left px-3 py-2 text-xs hover:bg-[var(--bg-2)] border-b border-[var(--line)]"
                    data-testid="best-exit-lead-option"
                  >
                    <div className="font-semibold truncate">{l.property_address || "Untitled"}</div>
                    <div className="text-[10px] text-[var(--ink-3)] mt-0.5">
                      {[l.city, l.state, l.stage].filter(Boolean).join(" · ")}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 3-up ranked summary */}
      <div className="grid grid-cols-3 gap-2 mt-4">
        {data.options.map((o, i) => {
          const isWinner = i === 0;
          return (
            <div
              key={o.strategy}
              className={`rounded-lg p-3 border text-center ${
                isWinner
                  ? "border-emerald-400 bg-white"
                  : "border-[var(--line)] bg-white/60"
              }`}
              data-testid={`best-exit-option-${o.strategy}`}
            >
              <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--ink-3)]">
                #{i + 1} · {o.label}
              </div>
              <div className={`font-display font-black text-lg tabular-nums mt-1 ${
                o.profit > 0 ? "text-[var(--ink)]" : "text-[var(--ink-3)]"
              }`}>
                {fmtMoney(o.profit)}
              </div>
              <div className="text-[10px] text-[var(--ink-3)] mt-1 leading-tight">
                {o.subtitle}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Wholesale → Novation/Wholetail cross-sell ─── */
function CrossSellCard({ wholesaleProfit, onTryNovation, onTryWholetail }) {
  return (
    <div className="card" data-testid="wholesale-crosssell" style={{
      background: "linear-gradient(135deg, rgba(245,158,11,0.08), rgba(244,63,94,0.08))",
      borderColor: "rgba(245,158,11,0.35)",
    }}>
      <div className="label-eyebrow" style={{ color: "#B45309" }}>Try a different exit</div>
      <h3 className="font-display font-bold text-lg mt-1 mb-2">
        Wholesale margin is thin ({fmtMoney(wholesaleProfit)}). Two faster exits:
      </h3>
      <p className="text-sm text-[var(--ink-2)] mb-4">
        Same property, different exit strategy — your inputs are pre-loaded.
      </p>
      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={onTryNovation}
          className="btn-primary text-sm"
          data-testid="crosssell-try-novation"
        >
          List on MLS (Novation) →
        </button>
        <button
          type="button"
          onClick={onTryWholetail}
          className="btn-outline text-sm"
          data-testid="crosssell-try-wholetail"
        >
          Light rehab + list (Wholetail) →
        </button>
      </div>
    </div>
  );
}

function ResultsCard({ title, metrics, empty, rec }) {
  return (
    <div className="card">
      <div className="label-eyebrow">Metrics</div>
      <h2 className="font-display font-bold text-xl mt-1 mb-5">{title}</h2>
      {!metrics ? (
        <div className="empty">
          <Calculator size={26} className="mx-auto mb-2 text-[var(--ink-3)]" />
          <div className="text-sm">{empty}</div>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            {metrics.map((m, i) => (
              <div key={i} className="card-flat p-4">
                <div className="text-[11px] uppercase tracking-widest text-[var(--ink-2)] font-semibold">{m.label}</div>
                <div className={"font-display font-bold mt-1 tabular-nums " + (m.big ? "text-2xl" : "text-xl") + (m.accent ? " text-[var(--primary)]" : "")}>{m.value}</div>
              </div>
            ))}
          </div>
          {rec && (
            <div className="card-flat p-4 flex items-center gap-3 text-sm">
              <TrendingUp size={16} className="text-[var(--primary)]" />
              <span className="font-semibold">{rec}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function InsightCard({ insight }) {
  const style = VERDICT_STYLE[insight.verdict] || {};
  return (
    <div className="card" data-testid="analyzer-ai-result">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="label-eyebrow">AI Deal Insight</div>
          <h2 className="font-display font-bold text-xl mt-1">Verdict & next steps</h2>
        </div>
        {insight.verdict && (
          <span className="chip" style={{ background: style.bg, color: style.color, borderColor: style.border, fontSize: 13, padding: "6px 14px" }} data-testid="analyzer-verdict">
            <span className="chip-dot" style={{ background: style.color }} /> {insight.verdict}
          </span>
        )}
      </div>
      <p className="text-[15px] leading-relaxed mb-5">{insight.summary}</p>
      <div className="grid md:grid-cols-2 gap-4">
        <Block icon={AlertTriangle} title="Risks" items={insight.risks} />
        <Block icon={ListChecks} title="Next steps" items={insight.next_steps} />
      </div>
    </div>
  );
}

function Block({ icon: Icon, title, items }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className="text-[var(--ink-2)]" />
        <div className="font-semibold text-sm">{title}</div>
      </div>
      <ul className="space-y-2">
        {(items || []).map((s, i) => (
          <li key={i} className="text-sm leading-relaxed flex items-start gap-2">
            <span className="text-[var(--primary)] font-mono mt-1">›</span>
            <span>{s}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
